import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { useManagedVenues } from '@/hooks/useManagedVenues'
import { useLogout } from '@/lib/auth/session'
import { localManagedVenueSource } from '@/lib/venue/managedVenue'
import type { ManagedVenueItem } from '@/lib/venue/managerVenueTypes'
import { venueHomePath } from '@/routes/postLogin'

/**
 * 관리할 구장 선택 — `GET /venue/v1/manager/venues` 소비 (설계 §2-1(d)).
 *
 * 🚨 구장 번호 수동 입력(갭 G1 임시 화면)을 대체한다. 이제 목록의 정본은 서버이고,
 * 화면은 서버가 준 상태값을 **재계산 없이 그대로** 보여준다.
 * 🚨 심사 대기·반려·노출 중단 구장도 **빠지지 않는다**(#584 §1). 상태는 배지로 알린다.
 */

function VenueCard({ venue, onSelect }: { venue: ManagedVenueItem; onSelect: () => void }) {
  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-ink-700">{venue.name}</p>
            <p className="mt-0.5 truncate text-sm text-slateish-600">{venue.address}</p>
          </div>
          {/* 🚨 '노출 중' 판정의 단일 출처는 서버 `userVisible` 이다 — 재계산 금지(#584 §1). */}
          <Badge tone={venue.userVisible ? 'success' : 'neutral'}>
            {venue.userVisible ? '노출 중' : '노출 안 됨'}
          </Badge>
        </div>

        <Button block onClick={onSelect}>
          이 구장 관리하기
        </Button>
      </CardBody>
    </Card>
  )
}

export function VenueSelectPage() {
  const navigate = useNavigate()
  const [logout, isLoggingOut] = useLogout()
  const venuesQuery = useManagedVenues()

  const venues = venuesQuery.data?.venues
  const onlyVenueId = venues?.length === 1 ? venues[0]?.venueId : undefined

  // 구장이 하나뿐이면 고를 이유가 없다 — 바로 들여보낸다(뒤로가기로 여기 되돌아오지 않게 replace).
  useEffect(() => {
    if (onlyVenueId === undefined) return
    localManagedVenueSource.remember(onlyVenueId)
    navigate(venueHomePath(onlyVenueId), { replace: true })
  }, [navigate, onlyVenueId])

  function select(venueId: string) {
    localManagedVenueSource.remember(venueId)
    navigate(venueHomePath(venueId), { replace: true })
  }

  return (
    <div className="mx-auto max-w-[560px] px-6 py-16">
      <PageHeader
        title="관리할 구장 선택"
        description="회원님의 계정에 연결된 구장입니다."
        action={
          <div className="flex items-center gap-2">
            {/* #749 — 다구장 관리자를 위한 보조 진입점. 주 진입점은 `AppShell` 사이드바(§2 "진입 경로"). */}
            {venues && venues.length > 0 ? (
              <Button
                variant="secondary"
                onClick={() => {
                  navigate('/venues/register')
                }}
              >
                새 구장 등록
              </Button>
            ) : null}
            <Button variant="ghost" onClick={() => void logout()} disabled={isLoggingOut}>
              {isLoggingOut ? '로그아웃 중' : '로그아웃'}
            </Button>
          </div>
        }
      />

      {venuesQuery.isLoading ? <LoadingState label="구장 목록을 불러오는 중" /> : null}

      {venuesQuery.isError ? (
        <ErrorState
          error={venuesQuery.error}
          onRetry={() => {
            void venuesQuery.refetch()
          }}
        />
      ) : null}

      {/* 🚨 연결 구장 0건은 에러가 아니라 정상 응답이다(#584 §1) — 빈 상태로 안내한다. */}
      {venues?.length === 0 ? (
        <Card>
          <EmptyState
            title="연결된 구장이 없습니다"
            description="구장 관리자 계정에 구장이 아직 연결되지 않았습니다. 페이스콕 담당자에게 문의해 주세요."
          />
        </Card>
      ) : null}

      <div className="space-y-3">
        {venues?.map((venue) => (
          <VenueCard
            key={venue.venueId}
            venue={venue}
            onSelect={() => {
              select(venue.venueId)
            }}
          />
        ))}
      </div>
    </div>
  )
}
